import { Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse,
    HttpResponse,
    HttpBackend,
    HttpClient
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { TokenStorageService } from '../services/token-storage.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/services/auth.service';
import { ApiUrls } from '../shared/api-urls';

interface GetAccessResponse {
    originalResponse: {
        StatusCode: number | null;
        Error: string | null;
        Message: string | null;
        Data: {
            ID: number;
            EmployeeID: number;
            EmpNo: number;
            MarkaziaUserID: string | null;
            FirstName: string | null;
            SecondName: string | null;
            ThirdName: string | null;
            LastName: string | null;
            Email: string | null;
            MobileNumber: string | null;
            JobTitle: string | null;
            Name: string | null;
            IsActivite: boolean | null;
            Status: string | null;
            GenderID: number | null;
            GroupID: number | null;
            BusinessAreaID: number | null;
            WorkplaceClassificationID: number | null;
            ContractTypeID: number | null;
            CreatedAt: string | null;
            ModifiedAt: string | null;
            CreatedBy: number | null;
            ModifiedBy: number | null;
            IsDeleted: boolean | null;
            UserWorkingHours: any[];
            UserBusinessAreas: any[];
            Group: any | null;
            UserRoles: any[];
        } | null;
        Status: boolean | null;
    };
    enhancedUserDetails?: {
        userId: number;
        employeeId: number;
        identifierId: number;
        mobile?: string | null;
        city?: string | null;
        branchInfo?: {
            branchId?: number;
            BranchId?: number;
            businessAreaID?: number;
            BusinessAreaID?: number;
            BranchName?: string;
            baName?: string;
            Name?: string;
            BranchNo?: string;
            baNo?: string;
            baCompany?: string;
            Company?: string;
            baGroup?: string;
            Group?: string;
            baLegalEntity?: string;
            LegalEntity?: string;
        };
        roles?: {
            roleId: number;
            roleName: string;
        };
        userDetails?: {
            fullName: string;
            userEmail: string | null;
            userJobTitle: string | null;
            profileImage: string | null;
            mainBusinessArea?: {
                businessAreaID: number;
                baName: string;
            };
        };
    };
}

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private getAccessEndpoint = `${environment.masterBaseUrl.replace(/\/+$/, '')}/${ApiUrls.User.GetAccess.replace(/^\/+/, '')}`;
    private rawHttp: HttpClient;
    private refreshEndpoint = `${environment.masterBaseUrl.replace(/\/+$/, '')}/${ApiUrls.User.RefreshThirdPartyToken.replace(/^\/+/, '')}`;

    constructor(
        private tokenStore: TokenStorageService,
        private authService: AuthService,
        private router: Router,
        handler: HttpBackend
    ) {
        this.rawHttp = new HttpClient(handler);
    }

    private isTokenEndpoint(url: string): boolean {
        return url.startsWith(this.getAccessEndpoint) || url.startsWith(this.refreshEndpoint);
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const localToken = this.tokenStore.token;
        const ssoAccess = this.tokenStore.ssoAccessToken;
        const inSsoMode = this.tokenStore.isSingleSignOnMode;

        const tokenToUse = this.isTokenEndpoint(req.url)
            ? (ssoAccess || localToken || null)
            : (localToken || ssoAccess || null);

        const authedReq = tokenToUse ? this.addAuth(req, tokenToUse) : req;

        return next.handle(authedReq).pipe(
            mergeMap(event => {
                if (event instanceof HttpResponse) {
                    const body = event.body;

                    if (
                        event.url?.startsWith(this.getAccessEndpoint) &&
                        body?.originalResponse?.Status === true
                    ) {
                        this.saveUserFromGetAccess(body as GetAccessResponse);
                    }

                    if (
                        body?.Status === false &&
                        (body?.Error === 'Token has expired' || body?.Message === 'Token has expired')
                    ) {
                        const ssoRefresh = this.tokenStore.ssoRefreshToken;
                        if (inSsoMode && ssoRefresh) {
                            return this.refreshAndRetry(req, next, ssoRefresh);
                        }
                    }
                }
                return of(event);
            }),

            catchError((err: any) => {
                return this.handleError(err, authedReq, next);
            })
        );
    }

    private saveUserFromGetAccess(res: GetAccessResponse) {
        const data = res?.originalResponse?.Data;
        const enhanced = res?.enhancedUserDetails;

        if (!data) return;

        const id =
            data.EmployeeID && data.EmployeeID !== 0
                ? data.EmployeeID
                : data.ID && data.ID !== 0
                    ? data.ID
                    : data.MarkaziaUserID ?? null;

        const fullName =
            enhanced?.userDetails?.fullName ||
            [data.FirstName, data.SecondName, data.ThirdName, data.LastName]
                .filter(Boolean)
                .join(' ') ||
            data.Email ||
            '';

        const email = enhanced?.userDetails?.userEmail || data.Email || '';

        this.tokenStore.saveUser({
            nameid: id,
            UserID: id,
            EmployeeID: data.EmployeeID ?? data.ID ?? null,
            EmpNo: data.EmpNo ?? null,
            Email: email,
            fullName,
            userPortals: [],
            role: enhanced?.roles?.roleName ?? null,
            city: enhanced?.city ?? null,
        });

        this.tokenStore.saveEmployeeInfo(res);

        try {
            const rawBranchInfo =
                enhanced?.branchInfo ??
                enhanced?.userDetails?.mainBusinessArea ??
                null;

            let branchId: number | null = null;
            let branch: {
                BranchId?: number;
                BranchName?: string | null;
                BranchNo?: string | null;
                Company?: string | null;
                Group?: string | null;
                LegalEntity?: string | null;
            } | null = null;

            if (typeof rawBranchInfo === 'number') {
                branchId = rawBranchInfo;
                branch = { BranchId: branchId };
            }

            else if (rawBranchInfo && typeof rawBranchInfo === 'object') {
                branchId =
                    ('branchId' in rawBranchInfo ? rawBranchInfo.branchId : undefined) ??
                    ('BranchId' in rawBranchInfo ? rawBranchInfo.BranchId : undefined) ??
                    ('businessAreaID' in rawBranchInfo ? rawBranchInfo.businessAreaID : undefined) ??
                    ('BusinessAreaID' in rawBranchInfo ? rawBranchInfo.BusinessAreaID : undefined) ??
                    enhanced?.identifierId ??
                    data.BusinessAreaID ??
                    null;

                branch = {
                    BranchId: branchId ?? undefined,
                    BranchName:
                        (rawBranchInfo as any).BranchName ??
                        (rawBranchInfo as any).baName ??
                        (rawBranchInfo as any).Name ??
                        null,
                    BranchNo:
                        (rawBranchInfo as any).BranchNo ??
                        (rawBranchInfo as any).baNo ??
                        null,
                    Company:
                        (rawBranchInfo as any).baCompany ??
                        (rawBranchInfo as any).Company ??
                        null,
                    Group:
                        (rawBranchInfo as any).baGroup ??
                        (rawBranchInfo as any).Group ??
                        null,
                    LegalEntity:
                        (rawBranchInfo as any).baLegalEntity ??
                        (rawBranchInfo as any).LegalEntity ??
                        null,
                };
            }

            if (!branchId) {
                branchId =
                    enhanced?.identifierId ??
                    data.BusinessAreaID ??
                    null;
            }

            if (!branch && branchId) {
                branch = { BranchId: branchId };
            }

            const role = enhanced?.roles
                ? {
                    RoleId: enhanced.roles.roleId,
                    RoleName: enhanced.roles.roleName?.trim() ?? '',
                }
                : null;

            const city = enhanced?.city ?? null;

            if (branch) localStorage.setItem('branch', JSON.stringify(branch));

            if (branch?.BranchId) {
                const branchIdStr = branch.BranchId.toString();
                localStorage.setItem('branchId', branchIdStr);
                localStorage.setItem('collectBranchId', branchIdStr);
            }

            if (city) localStorage.setItem('city', city);
            if (fullName) localStorage.setItem('fullName', fullName);
            if (role) localStorage.setItem('role', JSON.stringify(role));
            if (email) localStorage.setItem('userEmail', email);
            if (id) localStorage.setItem('userid', id.toString());

            if (data.BusinessAreaID)
                localStorage.setItem('userDepartmentId', data.BusinessAreaID.toString());

            localStorage.setItem('userPayload', JSON.stringify(res));
        } catch (err) {
            console.warn('[saveUserFromGetAccess] Failed to parse/store user fields:', err);
        }
    }

    private handleError(err: any, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (err instanceof HttpErrorResponse && err.status === 401) {
            const message = err?.error?.Message || err?.error?.Error || err?.message || '';
            const expired = /expired|invalid/i.test(message);

            if (expired) {
                const refreshToken = this.tokenStore.ssoRefreshToken;
                if (refreshToken) {
                    return this.refreshAndRetry(req, next, refreshToken);
                }
            }
        }
        return throwError(() => err);
    }

    private refreshAndRetry(req: HttpRequest<any>, next: HttpHandler, refreshToken: string): Observable<HttpEvent<any>> {
        const { isRefreshing, tokenSubject } = this.authService.getRefreshState();

        if (!isRefreshing) {
            return this.authService.refreshToken(refreshToken).pipe(
                switchMap(({ accessToken }) => {
                    const retried = this.addAuth(req, accessToken);
                    return next.handle(retried);
                }),
                catchError(e => throwError(() => e))
            );
        }

        return tokenSubject.pipe(
            filter(t => !!t),
            take(1),
            switchMap(t => next.handle(this.addAuth(req, t!)))
        );
    }


    isSsoMode = this.tokenStore.isSingleSignOnMode;

    /**
* Dynamically attaches the correct header for local or SSO authentication.
* - Uses "HubAuthorization" in SSO mode
* - Uses "Authorization" in normal mode
* - Skips adding auth header for GetAccess or RefreshThirdPartyToken requests
*/
    private addAuth(req: HttpRequest<any>, token: string): HttpRequest<any> {
        const url = req.url.toLowerCase();

        if (url.includes('getaccess') || url.includes('refreshthirdpartytoken')) {
            return req;
        }

        const headerName = this.isSsoMode ? 'HubAuthorization' : 'Authorization';

        return req.clone({
            setHeaders: { [headerName]: `Bearer ${token}` }
        });
    }
}
